<template>
  <smart-filters
    v-model="smartFiltersModel"
    :schema="filterSchema"
    :default-filters="defaultFilters"
    storage-key="requests"
    @filter="filtrar"
    @clear="LimpiarFiltros"
  />
</template>

<script setup>
import {
  computed,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import SmartFilters from '@/shared/components/smart_filters.vue';
import { useRequestStore } from '../stores/request_store.js';
import { useAuthStore } from '@/views/auth/auth_store';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import { useEjecutivoStore } from '@/stores/ejecutivo_store.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useProcesoStore } from '@/stores/proceso_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import request_filters_model from '../models/request_filters_model.js';

const requestStore = useRequestStore();
const authStore = useAuthStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const elementoTipoStore = useElementoTipoStore();
const ejecutivoStore = useEjecutivoStore();
const usabilidadStore = useUsabilidadStore();
const procesoStore = useProcesoStore();
const prospectStore = useProspectStore();

const usuario = authStore.usuario();
const { filters } = storeToRefs(requestStore);
const { findAllSolicitudesHTTP, setPage, emptyList, resetFilter } = requestStore;
const { searchProspectsHTTP } = prospectStore;
const defaultFilters = JSON.parse(JSON.stringify(request_filters_model));
const esEjecutivoDeVentas = computed(() => usuario.roles.some((rol) => rol.Codigo === 1));

const filterSchema = computed(() => [
  {
    key: 'FechaCreacionRange',
    label: 'Fecha Solicitud',
    type: 'date_range',
    keyDesde: 'FechaDesde',
    keyHasta: 'FechaHasta',
    fixed: true,
    span: 6,
  },
  {
    key: 'CodigoUsuarioCreacion',
    label: 'Ejecutivo',
    type: 'select',
    placeholder: 'Seleccionar un ejecutivo',
    fetchOptions: async () => {
      const results = await ejecutivoStore.searchEjecutivosHTTP('');
      return (results || []).map((u) => ({
        ...u,
        NombreCompleto: `${u.Nombre || ''} ${u.Apellido || ''}`.trim(),
      }));
    },
    fieldKey: 'Codigo',
    fieldLabel: 'NombreCompleto',
    span: 4,
    fixed: esEjecutivoDeVentas.value,
    disabled: esEjecutivoDeVentas.value,
  },
  {
    key: 'EstadoSolicitud',
    label: 'Estado',
    type: 'select',
    placeholder: 'Seleccionar estado',
    options: estadoSolicitudStore.estadoSolicitudList,
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'Cliente',
    label: 'Cliente',
    type: 'autocomplete',
    placeholder: 'Ingresar cliente',
    fieldLabel: 'Descripcion',
    searchFn: async (query) => {
      if (query && query.length >= 3 && query !== 'null') {
        const results = await searchProspectsHTTP(query);

        return results.map((item) => ({
          ...item,
          value: item.Descripcion,
        }));
      }
      return [];
    },
    span: 4,
  },
  {
    key: 'CodigoEspecificacionTecnica',
    label: 'Cód. ET',
    type: 'input',
    placeholder: 'Ej: 3401, 3402',
    span: 3,
  },
  {
    key: 'CodigoEquivalente',
    label: 'Cód. Equivalente',
    type: 'input',
    placeholder: 'Ej: 123-A',
    span: 3,
  },
  {
    key: 'ElementoTipo',
    label: 'Tipo de producto',
    type: 'select',
    placeholder: 'Seleccionar',
    options: elementoTipoStore.productTypeList,
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    multiple: true,
    span: 4,
  },
  {
    key: 'FechaEstadoRange',
    label: 'Fecha Estado',
    type: 'date_range',
    keyDesde: 'FechaEstadoDesde',
    keyHasta: 'FechaEstadoHasta',
    span: 6,
  },
  {
    key: 'Referencia',
    label: 'Producto',
    type: 'input',
    placeholder: 'Ingresar producto',
    span: 4,
  },
  {
    key: 'CodigoAdicional',
    label: 'Cód. Adicional',
    type: 'input',
    placeholder: 'Ej: SK-900',
    span: 4,
  },
  {
    key: 'DescripcionComercial',
    label: 'Descripción Material',
    type: 'input',
    placeholder: 'Ej: térmico, bpp',
    span: 4,
  },
  {
    key: 'CodigoUsabilidad',
    label: 'Usabilidad',
    type: 'select',
    placeholder: 'Seleccionar usabilidad',
    fetchOptions: async () => {
      await usabilidadStore.findAllUsabilidadHTTP({ page: 0 });
      return usabilidadStore.usabilidadLista;
    },
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'Suaje',
    label: 'Suaje',
    type: 'input',
    placeholder: 'Ej: RM228',
    span: 4,
  },
  {
    key: 'Procesos',
    label: 'Procesos',
    type: 'select',
    placeholder: 'Seleccionar procesos',
    multiple: true,
    fetchOptions: async () => {
      await procesoStore.findAllProcesosHTTP({ page: 0, Activo: true });
      return procesoStore.procesoList;
    },
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 5,
  },
  {
    key: 'Codigo',
    label: 'Nro. Solicitud',
    type: 'input',
    placeholder: 'Ej: 100, 101',
    span: 3,
  },
]);

const formatearFechaParaUI = (val) => {
  if (!val) return null;
  const d = new Date(val);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const smartFiltersModel = computed({
  get: () => {
    const sf = {};
    filterSchema.value.forEach((f) => {
      if (f.type === 'date_range') {
        const desde = filters.value[f.keyDesde]
          ? formatearFechaParaUI(filters.value[f.keyDesde])
          : null;
        const hasta = filters.value[f.keyHasta]
          ? formatearFechaParaUI(filters.value[f.keyHasta])
          : null;

        if (f.fixed || desde || hasta) {
          sf[f.key] = { field: f, value: [desde, hasta] };
        }
      } else if (f.key === 'CodigoUsuarioCreacion') {
        if (
          filters.value.CodigoUsuarioCreacion !== null &&
          filters.value.CodigoUsuarioCreacion !== undefined
        ) {
          const nombreAExhibir = esEjecutivoDeVentas.value
            ? `${usuario.Nombre} ${usuario.Apellido}`.trim()
            : '';
          sf[f.key] = {
            field: f,
            value: { Codigo: filters.value.CodigoUsuarioCreacion, NombreCompleto: nombreAExhibir },
          };
        }
      } else if (f.key === 'EstadoSolicitud') {
        const val = filters.value.EstadoSolicitud || filters.value.Estado;
        if (val && val.Codigo !== null && val.Codigo !== undefined) {
          sf[f.key] = { field: f, value: val };
        }
      } else if (f.key === 'Cliente') {
        if (filters.value.Cliente) {
          sf[f.key] = { field: f, value: { Descripcion: filters.value.Cliente } };
        }
      } else if (f.key === 'ElementoTipo') {
        if (filters.value.ElementoTipo && filters.value.ElementoTipo.length > 0) {
          sf[f.key] = { field: f, value: filters.value.ElementoTipo };
        }
      } else if (f.key === 'CodigoUsabilidad') {
        if (
          filters.value.CodigoUsabilidad !== null &&
          filters.value.CodigoUsabilidad !== undefined
        ) {
          sf[f.key] = { field: f, value: { Codigo: filters.value.CodigoUsabilidad } };
        }
      } else if (f.key === 'Procesos') {
        if (filters.value.Procesos && filters.value.Procesos.length > 0) {
          sf[f.key] = { field: f, value: filters.value.Procesos };
        }
      } else {
        if (
          filters.value[f.key] !== null &&
          filters.value[f.key] !== undefined &&
          filters.value[f.key] !== ''
        ) {
          sf[f.key] = { field: f, value: filters.value[f.key] };
        }
      }
    });
    return sf;
  },
  set: (newSf) => {
    const baseEstructura = {
      FechaDesde: null,
      FechaHasta: null,
      EstadoSolicitud: null,
      Estado: null,
      CodigoUsuarioCreacion: null,
      FechaEstadoDesde: null,
      FechaEstadoHasta: null,
      Cliente: null,
      Referencia: null,
      CodigoEspecificacionTecnica: null,
      ElementoTipo: [],
      CodigoEquivalente: null,
      CodigoAdicional: null,
      DescripcionComercial: null,
      CodigoProducto: null,
      CodigoUsabilidad: null,
      Procesos: [],
      Codigo: null,
    };

    Object.values(newSf).forEach((item) => {
      if (!item || !item.field) return;

      if (item.field.type === 'date_range') {
        baseEstructura[item.field.keyDesde] = item.value?.[0]
          ? new Date(`${item.value[0]}T00:00:00`)
          : null;
        baseEstructura[item.field.keyHasta] = item.value?.[1]
          ? new Date(`${item.value[1]}T23:59:59`)
          : null;
      } else if (item.field.key === 'CodigoUsuarioCreacion') {
        const val = item.value;
        baseEstructura.CodigoUsuarioCreacion =
          typeof val === 'object' && val !== null ? val.Codigo : (val ?? null);
      } else if (item.field.key === 'EstadoSolicitud') {
        baseEstructura.EstadoSolicitud = item.value;
        baseEstructura.Estado = item.value;
      } else if (item.field.key === 'CodigoUsabilidad') {
        const val = item.value;
        baseEstructura.CodigoUsabilidad =
          typeof val === 'object' && val !== null ? val.Codigo : (val ?? null);
      } else if (item.field.key === 'Cliente') {
        const val = item.value;
        baseEstructura.Cliente =
          typeof val === 'object' && val !== null ? val.Descripcion : (val ?? null);
      } else if (item.field.key === 'Procesos') {
        baseEstructura.Procesos = Array.isArray(item.value) ? item.value : [];
      } else {
        baseEstructura[item.field.key] = item.value;
      }
    });

    if (esEjecutivoDeVentas.value) {
      baseEstructura.CodigoUsuarioCreacion = usuario.Codigo;
    }

    for (const key in baseEstructura) {
      requestStore.filters[key] = baseEstructura[key];
    }
  },
});

const filtrar = () => {
  emptyList();
  setPage(1);
  findAllSolicitudesHTTP();
};

const LimpiarFiltros = () => {
  resetFilter();
  if (esEjecutivoDeVentas.value) {
    requestStore.filters.CodigoUsuarioCreacion = usuario.Codigo;
  }
  filtrar();
};

onBeforeMount(async () => {
  await estadoSolicitudStore.firstEstadoSolicitud();

  if (esEjecutivoDeVentas.value && !filters.value.CodigoUsuarioCreacion) {
    requestStore.filters.CodigoUsuarioCreacion = usuario.Codigo;
  }
});
</script>

<style scoped>
/* Los estilos del container ahora los hereda y domina el smart_filters.vue interno */
</style>
